/**
 * The donor's issue screen (phase5-design.md §g b, "gift codes").
 *
 * A donor mints a batch of codes straight from the available balance. The page
 * sends seats and a count and nothing else — the server prices the batch from
 * its own settings, the same discipline as the checkout: no amount the browser
 * computes is ever trusted.
 *
 * The response's tokens are the one display they will ever get (§a): after
 * this screen they exist only as a hash on the server. So this page is built
 * around taking them away — copy each, share each on WhatsApp, download the
 * batch as CSV — and it says so in as many words.
 */
import { ApiRequestError, apiFetch } from '@qalam/shared/frontend';
import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { buildCodesCsv, previewIssue } from '../checkout/codesFlow';
import type { MyCodesResponse } from './MyCodesPage';

export interface IssuedCode {
  id: string;
  /** The token, in the response that carries it for the only time. */
  token: string;
  hint: string;
  seats: number;
  expiresAt: string;
}

export interface IssueCodesResponse {
  items: IssuedCode[];
  totalCents: string;
  seatPriceCents: string;
}

/**
 * Hand the CSV to the browser. A data URL would carry the tokens into browser
 * history; a blob URL is memory-only and revoked as soon as the click lands.
 */
function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const fieldStyle: CSSProperties = { display: 'grid', gap: 'var(--space-1)', maxWidth: '16rem' };

export default function IssueCodesPage() {
  const { t } = useTranslation();
  const [wallet, setWallet] = useState<MyCodesResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [seats, setSeats] = useState(1);
  const [count, setCount] = useState(1);
  const [expiresInDays, setExpiresInDays] = useState<string>('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedCode[] | null>(null);
  const [copiedIds, setCopiedIds] = useState<ReadonlySet<string>>(new Set());

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setWallet(await apiFetch<MyCodesResponse>('/api/my/codes'));
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The preview is display-only (see codesFlow.previewIssue): the batch is
  // priced server-side, so a stale seat price here can mislead an eye but
  // never a ledger.
  const preview =
    wallet === null
      ? null
      : previewIssue({ seats, count, seatPriceCents: Number(wallet.seatPriceCents) });

  async function submit(): Promise<void> {
    setWorking(true);
    setError(null);
    try {
      const parsedDays = Number.parseInt(expiresInDays, 10);
      const result = await apiFetch<IssueCodesResponse>('/api/my/codes/issue', {
        method: 'POST',
        body: {
          seats,
          count,
          ...(Number.isInteger(parsedDays) && parsedDays > 0 ? { expiresInDays: parsedDays } : {}),
        },
      });
      setIssued(result.items);
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'insufficient_balance'
          ? t('issueCodes.insufficientBalance')
          : t('issueCodes.failed'),
      );
    } finally {
      setWorking(false);
    }
  }

  // The once-screen: the minted batch, built for taking away.
  if (issued !== null) {
    const csv = buildCodesCsv(issued.map((item) => ({ token: item.token, seats: item.seats })));
    const stamp = new Date().toISOString().slice(0, 10);
    return (
      <main
        data-testid="issue-codes-done"
        style={{
          maxWidth: 'var(--content-narrow)',
          marginInline: 'auto',
          paddingInline: 'var(--space-4)',
          paddingBlock: 'var(--space-6)',
        }}
      >
        <h1>{t('issueCodes.doneTitle')}</h1>
        <p
          data-testid="once-warning"
          style={{
            paddingInline: 'var(--space-3)',
            paddingBlock: 'var(--space-2)',
            background: 'var(--qalam-warning-100)',
            color: 'var(--color-warning)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {t('issueCodes.onceWarning')}
        </p>
        <ul
          style={{
            listStyle: 'none',
            paddingInlineStart: 0,
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          {issued.map((item) => (
            <li
              key={item.id}
              style={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                paddingInline: 'var(--space-4)',
                paddingBlock: 'var(--space-3)',
                display: 'grid',
                gap: 'var(--space-2)',
              }}
            >
              <code
                data-testid="issued-token"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-sm)',
                  wordBreak: 'break-all',
                  direction: 'ltr',
                  unicodeBidi: 'isolate',
                }}
              >
                {item.token}
              </code>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  data-testid="copy-token"
                  onClick={() => {
                    void navigator.clipboard?.writeText(item.token).then(() => {
                      setCopiedIds((current) => new Set(current).add(item.id));
                    });
                  }}
                >
                  {copiedIds.has(item.id) ? t('issueCodes.copied') : t('issueCodes.copyToken')}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    t('issueCodes.shareMessage', { seats: item.seats, token: item.token }),
                  )}`}
                  data-testid="share-token-whatsapp"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('issueCodes.shareToken')}
                </a>
              </div>
            </li>
          ))}
        </ul>
        <button
          type="button"
          data-testid="download-csv"
          onClick={() => downloadCsv(`qalam-codes-${stamp}.csv`, csv)}
        >
          {t('issueCodes.downloadCsv')}
        </button>
      </main>
    );
  }

  return (
    <main
      data-testid="issue-codes"
      style={{
        maxWidth: 'var(--content-narrow)',
        marginInline: 'auto',
        paddingInline: 'var(--space-4)',
        paddingBlock: 'var(--space-6)',
      }}
    >
      <h1>{t('issueCodes.title')}</h1>
      <p>{t('issueCodes.intro')}</p>

      {loadFailed ? (
        <p>
          {t('common.error')}{' '}
          <button type="button" onClick={() => void load()}>
            {t('app.retry')}
          </button>
        </p>
      ) : wallet === null ? (
        <p>{t('app.loading')}</p>
      ) : (
        <>
          <p data-testid="issue-balance">
            {t('myCodes.balance', { amount: (Number(wallet.balanceCents) / 100).toFixed(2) })}
          </p>
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            <label htmlFor="issue-seats" style={fieldStyle}>
              {t('issueCodes.seatsPerCode')}
              <input
                id="issue-seats"
                type="number"
                min={1}
                max={1000}
                step={1}
                value={seats}
                onChange={(event) => setSeats(Number(event.target.value))}
              />
            </label>
            <label htmlFor="issue-count" style={fieldStyle}>
              {t('issueCodes.howMany')}
              <input
                id="issue-count"
                type="number"
                min={1}
                max={50}
                step={1}
                value={count}
                onChange={(event) => setCount(Number(event.target.value))}
              />
            </label>
            <label htmlFor="issue-days" style={fieldStyle}>
              {t('issueCodes.expiresInDays')}
              <input
                id="issue-days"
                type="number"
                min={1}
                max={365}
                step={1}
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(event.target.value)}
              />
            </label>
          </div>

          {preview !== null ? (
            <p data-testid="issue-preview" style={{ fontWeight: 600 }}>
              {t('issueCodes.preview', {
                codes: preview.totalCodes,
                seats: preview.totalSeats,
                amount: (preview.totalCents / 100).toFixed(2),
              })}
            </p>
          ) : null}

          <button
            type="button"
            data-testid="issue-submit"
            disabled={working || seats < 1 || count < 1}
            onClick={() => void submit()}
          >
            {working ? t('issueCodes.working') : t('issueCodes.issue')}
          </button>

          {error ? <p data-testid="issue-error">{error}</p> : null}
        </>
      )}

      <p style={{ marginBlockStart: 'var(--space-6)' }}>
        <Link to="/my-codes" data-testid="back-to-my-codes">
          {t('issueCodes.backToList')}
        </Link>
      </p>
    </main>
  );
}
